import React from "react";
import {
  AssetUnsecuredDetails,
  AssetInterestRateRow,
  AssetFeeCharge,
  commonIncomeVerificationOptions,
  commonAtoDebtOptions,
  commonCreditProfileOptions,
  commonFundingSpeedOptions,
  commonMinimumTimeTradingOptions,
  unsecuredPrimaryPurposeOptions,
  formatCurrencyWithCommasInput,
  formatIntegerInput,
  formatTwoDecimalInput,
  finalizeTwoDecimalInput,
  toggleOptionInArray,
  assetSecuredIncomeVerificationOptions,
  securedIndustryExcludedOptions,
} from "./loanProductUtils";

type Props = {
  details: AssetUnsecuredDetails;
  onChange: (next: AssetUnsecuredDetails) => void;
};

/* ─── helpers ─── */
const parseCurrency = (v: string) => parseFloat(v.replace(/[$,]/g, "")) || 0;
const parseNum = (v: string) => parseFloat(v) || 0;

/* ─── Inline checkbox row: label left, checkboxes right ─── */
const InlineCheckboxRow = ({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (value: string, checked: boolean) => void;
}) => (
  <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "10px" }}>
    <div style={{ minWidth: "180px", fontWeight: 500, paddingTop: "2px", fontSize: "14px" }}>
      {label}
    </div>
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 20px" }}>
      {options.map((option) => (
        <div key={option} className="form-check" style={{ margin: 0, minWidth: "160px" }}>
          <input
            className="form-check-input"
            type="checkbox"
            id={`${label}-${option}`}
            checked={selected.includes(option)}
            onChange={(e) => onToggle(option, e.target.checked)}
          />
          <label className="form-check-label" htmlFor={`${label}-${option}`} style={{ fontSize: "13px" }}>
            {option}
          </label>
        </div>
      ))}
    </div>
  </div>
);

/* ─── Inline Yes/No row: label left, select right ─── */
const InlineYesNoRow = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: "" | "Yes" | "No";
  onChange: (value: "" | "Yes" | "No") => void;
}) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
    <div style={{ minWidth: "180px", fontWeight: 500, fontSize: "14px" }}>{label}</div>
    <select
      className="form-select"
      style={{ width: "110px", fontSize: "13px", padding: "3px 6px", height: "32px" }}
      value={value}
      onChange={(e) => onChange(e.target.value as "" | "Yes" | "No")}
    >
      <option value="Yes">Yes</option>
      <option value="No">No</option>
    </select>
  </div>
);

/* ─── Inline Radio row ─── */
const InlineRadioRow = ({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string;
  onChange: (val: string) => void;
}) => (
  <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "10px" }}>
    <div style={{ minWidth: "180px", fontWeight: 500, paddingTop: "2px", fontSize: "14px" }}>
      {label}
    </div>
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 20px" }}>
      {options.map((option) => (
        <div key={option} className="form-check" style={{ margin: 0, minWidth: "160px" }}>
          <input
            className="form-check-input"
            type="radio"
            name={label}
            id={`${label}-${option}`}
            checked={selected === option}
            onChange={() => onChange(option)}
          />
          <label className="form-check-label" htmlFor={`${label}-${option}`} style={{ fontSize: "13px" }}>
            {option}
          </label>
        </div>
      ))}
    </div>
  </div>
);

const AssetUnsecuredProductTypeFields = ({ details, onChange }: Props) => {
  const updateFee = (index: number, patch: Partial<AssetFeeCharge>) => {
    onChange({
      ...details,
      feesAndCharges: details.feesAndCharges.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    });
  };

  const updateRate = (index: number, patch: Partial<AssetInterestRateRow>) => {
    onChange({
      ...details,
      interestRates: details.interestRates.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    });
  };

  /* ── validation ── */
  const amountMaxInvalid =
    details.loanAmountMin !== "" &&
    details.loanAmountMax !== "" &&
    parseCurrency(details.loanAmountMax) <= parseCurrency(details.loanAmountMin);

  const termMaxInvalid =
    details.loanTermMonthsMin !== "" &&
    details.loanTermMonthsMax !== "" &&
    parseNum(details.loanTermMonthsMax) <= parseNum(details.loanTermMonthsMin);

  /* ── shared input styles ── */
  const inputCurrency: React.CSSProperties = {
    width: "10ch",
    fontSize: "13px",
    padding: "3px 6px",
    height: "32px",
    display: "inline-block",
  };
  const inputInt: React.CSSProperties = {
    width: "6ch",
    fontSize: "13px",
    padding: "3px 6px",
    height: "32px",
    display: "inline-block",
  };
  const inputPct: React.CSSProperties = {
    width: "7ch",
    fontSize: "13px",
    padding: "3px 6px",
    height: "32px",
    display: "inline-block",
  };

  const labelStyle: React.CSSProperties = { minWidth: "180px", fontWeight: 500, fontSize: "14px" };
  const rowStyle: React.CSSProperties = { display: "flex", alignItems: "center", marginBottom: "10px", flexWrap: "wrap" as const, gap: "6px" };
  const inlineLabel: React.CSSProperties = { fontSize: "13px", color: "#444" };
  const errorBorder: React.CSSProperties = { borderColor: "#dc3545", boxShadow: "0 0 0 .15rem rgba(220,53,69,.25)" };

  return (
    <div className="col-12 border rounded p-3 mt-2">

      {/* ══════════════ LOAN DETAILS ══════════════ */}
      <h5 className="text-primary mb-3 fw-bold" style={{ borderBottom: '2px solid #0d6efd', paddingBottom: '5px' }}>Loan Details</h5>

      {/* Loan Amount */}
      <div style={rowStyle}>
        <span style={labelStyle}>Loan Amount</span>
        <span style={inlineLabel}>Minimum</span>
        <input
          className="form-control loan-compact-input"
          style={inputCurrency}
          value={details.loanAmountMin}
          onChange={(e) => onChange({ ...details, loanAmountMin: formatCurrencyWithCommasInput(e.target.value) })}
          placeholder="$99,999,999"
        />
        <span style={{ ...inlineLabel, marginLeft: "16px" }}>Maximum</span>
        <input
          className="form-control loan-compact-input"
          style={{ ...inputCurrency, ...(amountMaxInvalid ? errorBorder : {}) }}
          value={details.loanAmountMax}
          onChange={(e) => onChange({ ...details, loanAmountMax: formatCurrencyWithCommasInput(e.target.value) })}
          placeholder="$99,999,999"
          title={amountMaxInvalid ? "Maximum must be greater than Minimum" : undefined}
        />
        {amountMaxInvalid && (
          <span style={{ fontSize: "12px", color: "#dc3545" }}>Max must be &gt; Min</span>
        )}
      </div>

      {/* Loan Term Months */}
      <div style={rowStyle}>
        <span style={labelStyle}>Loan Term Months</span>
        <span style={inlineLabel}>Minimum</span>
        <input
          className="form-control loan-compact-input"
          style={inputInt}
          value={details.loanTermMonthsMin}
          onChange={(e) => onChange({ ...details, loanTermMonthsMin: formatIntegerInput(e.target.value, 3) })}
          placeholder="99"
        />
        <span style={{ ...inlineLabel, marginLeft: "16px" }}>Maximum</span>
        <input
          className="form-control loan-compact-input"
          style={{ ...inputInt, ...(termMaxInvalid ? errorBorder : {}) }}
          value={details.loanTermMonthsMax}
          onChange={(e) => onChange({ ...details, loanTermMonthsMax: formatIntegerInput(e.target.value, 3) })}
          placeholder="999"
          title={termMaxInvalid ? "Maximum must be greater than Minimum" : undefined}
        />
        {termMaxInvalid && (
          <span style={{ fontSize: "12px", color: "#dc3545" }}>Max must be &gt; Min</span>
        )}
      </div>



      {/* Minimum Monthly Turnover */}
      <div style={rowStyle}>
        <span style={labelStyle}>Minimum Monthly Turnover</span>
        <input
          className="form-control loan-compact-input"
          style={inputCurrency}
          value={details.minimumMonthlyTurnover}
          onChange={(e) => onChange({ ...details, minimumMonthlyTurnover: formatCurrencyWithCommasInput(e.target.value) })}
          placeholder="$99,999,999"
        />
      </div>

      {/* Minimum Time Trading */}
      <InlineCheckboxRow
        label="Minimum Time Trading"
        options={commonMinimumTimeTradingOptions}
        selected={details.minimumTimeTrading}
        onToggle={(v, c) => onChange({ ...details, minimumTimeTrading: toggleOptionInArray(details.minimumTimeTrading, v, c) })}
      />

      {/* Primary Purpose */}
      <InlineCheckboxRow
        label="Primary Purpose"
        options={unsecuredPrimaryPurposeOptions}
        selected={details.primaryPurpose}
        onToggle={(v, c) => onChange({ ...details, primaryPurpose: toggleOptionInArray(details.primaryPurpose, v, c) })}
      />










      {/* ══════════════ BORROWER PROFILE ══════════════ */}
      <h5 className="text-primary mt-4 mb-3 fw-bold" style={{ borderBottom: '2px solid #0d6efd', paddingBottom: '5px' }}>Borrower Profile</h5>

      {/* Income Verification */}
      <InlineRadioRow
        label="Income Verification"
        options={assetSecuredIncomeVerificationOptions}
        selected={details.incomeVerification}
        onChange={(v) => onChange({ ...details, incomeVerification: v })}
      />

      {/* ATO Debt */}
      <InlineCheckboxRow
        label="ATO Debt"
        options={commonAtoDebtOptions}
        selected={details.atoDebt}
        onToggle={(v, c) => onChange({ ...details, atoDebt: toggleOptionInArray(details.atoDebt, v, c) })}
      />

      {/* Credit Profile */}
      <InlineCheckboxRow
        label="Credit Profile"
        options={commonCreditProfileOptions}
        selected={details.creditProfile}
        onToggle={(v, c) => onChange({ ...details, creditProfile: toggleOptionInArray(details.creditProfile, v, c) })}
      />







      <div style={{ display: "flex", gap: "40px", flexWrap: "wrap", marginTop: "30px", borderBottom: '2px solid #0d6efd', paddingBottom: '10px', marginBottom: "20px" }}>
        <div style={{ flex: "1 1 100%" }}>
          <h5 className="text-primary fw-bold m-0">Fees &amp; Charges</h5>
        </div>
      </div>

      <div style={{ display: "flex", gap: "40px", flexWrap: "wrap" }}>
        {/* ══════════════ FEES & CHARGES ══════════════ */}
        <div style={{ flex: "1 1 600px" }}>
          <div>
            {details.feesAndCharges.map((fee, index) => (
              <div
                key={fee.label}
                style={{ display: "flex", alignItems: "center", marginBottom: "8px", gap: "8px", flexWrap: "wrap" }}
              >
                <div style={{ minWidth: "160px", fontSize: "14px", fontWeight: 500 }}>{fee.label}</div>
                <input
                  className="form-control loan-compact-input"
                  style={inputCurrency}
                  value={fee.amount}
                  onChange={(e) => updateFee(index, { amount: formatCurrencyWithCommasInput(e.target.value) })}
                  placeholder="$999,999"
                />
                <span style={{ fontSize: "13px", fontWeight: 600 }}>OR</span>
                <input
                  className="form-control loan-compact-input"
                  style={inputPct}
                  value={fee.percent}
                  onChange={(e) => updateFee(index, { percent: formatTwoDecimalInput(e.target.value, 2) })}
                  onBlur={(e) => updateFee(index, { percent: finalizeTwoDecimalInput(e.target.value, 2) })}
                  placeholder="99.99"
                />
                <span style={{ fontSize: "13px", color: "#555" }}>of loan amount</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════ INTEREST RATE ══════════════ */}
      <h5 className="text-primary mt-4 mb-3 fw-bold" style={{ borderBottom: '2px solid #0d6efd', paddingBottom: '5px', width: '100%' }}>Interest Rate</h5>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "auto" }}>
          {/* ── Header ── */}
          <thead>
            <tr>
              {/* empty asset label col */}
              <th
                style={{
                  width: "180px",
                  fontWeight: 700,
                  color: "#0d6efd",
                  fontSize: "13px",
                  padding: "4px 12px 6px 8px",
                  textAlign: "left",
                  borderBottom: "1px solid #dee2e6",
                  whiteSpace: "nowrap",
                }}
              >
                Name
              </th>              <th
                style={{
                  width: "25%",
                  fontWeight: 700,
                  color: "#0d6efd",
                  fontSize: "13px",
                  padding: "4px 12px 6px 8px",
                  textAlign: "left",
                  borderBottom: "1px solid #dee2e6",
                  whiteSpace: "nowrap",
                }}
              >
                Interest Rate
              </th>
            </tr>
          </thead>

          {/* ── Rows ── */}
          <tbody>
            {details.interestRates.map((row, index) => (
              <tr key={row.label}>
                {/* Asset label */}
                <td
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                    padding: "6px 8px 6px 0",
                    borderBottom: "1px solid #dee2e6",
                    whiteSpace: "nowrap",
                          width: "2%"
                  }}
                >
                  {row.label}
                </td>

                {/* From → To */}
                <td style={{ padding: "6px 8px", borderBottom: "1px solid #dee2e6" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "nowrap" }}>
                    <span style={{ fontSize: "12px", color: "#555" }}>From</span>
                    <input
                      className="form-control loan-compact-input"
                      style={inputPct}
                      value={row.from}
                      onChange={(e) => updateRate(index, { from: formatTwoDecimalInput(e.target.value, 2) })}
                      onBlur={(e) => updateRate(index, { from: finalizeTwoDecimalInput(e.target.value, 2) })}
                      placeholder="99.99%"
                    />
                    <span style={{ fontSize: "12px", color: "#555" }}>To</span>
                    <input
                      className="form-control loan-compact-input"
                      style={inputPct}
                      value={row.to}
                      onChange={(e) => updateRate(index, { to: formatTwoDecimalInput(e.target.value, 2) })}
                      onBlur={(e) => updateRate(index, { to: finalizeTwoDecimalInput(e.target.value, 2) })}
                      placeholder="99.99%"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default AssetUnsecuredProductTypeFields;
